/**
 * Comptes de la plateforme — écran repris de la version du client (itération 36).
 *
 * Le super administrateur crée, modifie et retire des comptes dans n'importe quel
 * sous-compte. C'est ce qui permet d'ouvrir un accès à un nouvel opérateur sans lui
 * demander de le faire lui-même le premier jour.
 *
 * Deux adaptations : notre vocabulaire — un « opérateur » chez lui est un
 * « sous-compte » chez nous, et les routes suivent — et notre politique de mot de
 * passe, dix caractères et non six. Copier une version antérieure ne justifie pas
 * d'affaiblir un contrôle de sécurité, et le serveur refuse de toute façon.
 */
import { useEffect, useState } from 'react';
import api from '@/shared/api/client';
import { toast } from 'sonner';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Button } from '@/shared/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/shared/ui/dialog';
import { Search, Pencil, KeyRound, Trash2, Power, UserPlus } from 'lucide-react';
import BoutonMotDePasse from '@/shared/components/BoutonMotDePasse';

const ROLE_LABEL = {
  admin: 'Admin opérateur', commercial: 'Gestionnaire', distributeur: 'Distributeur', installateur: 'Installateur',
};

const ROLE_OPTIONS = ['admin', 'commercial', 'distributeur', 'installateur'];

export default function SuperAdminUsers() {
  const [users, setUsers] = useState([]);
  const [operators, setOperators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState(null); // user en cours d'édition
  const [editForm, setEditForm] = useState({ name: '', email: '', role: '', active: true, tenant_id: '', phone: '' });
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(null); // user pour reset password
  const [newPassword, setNewPassword] = useState('');
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: '', email: '', password: '', role: 'commercial', tenant_id: '', active: true, phone: '',
  });
  const [creatingSave, setCreatingSave] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [ur, or] = await Promise.all([
        api.get('/superadmin/users'),
        api.get('/tenants'),
      ]);
      setUsers(ur.data);
      setOperators(or.data);
    } catch { toast.error('Chargement impossible'); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const filtered = users.filter(u => {
    if (!q.trim()) return true;
    const s = q.trim().toLowerCase();
    return (u.email || '').toLowerCase().includes(s) ||
      (u.name || '').toLowerCase().includes(s) ||
      (u.tenant_nom || '').toLowerCase().includes(s);
  });

  const openEdit = (u) => {
    setEditing(u);
    setEditForm({
      name: u.name || '',
      email: u.email || '',
      role: u.role || 'commercial',
      active: u.active !== false,
      tenant_id: u.tenant_id || '',
      phone: u.phone || '',
    });
  };

  const saveEdit = async (e) => {
    e?.preventDefault?.();
    if (!editing) return;
    setSaving(true);
    try {
      await api.put(`/superadmin/users/${editing.id}`, editForm);
      toast.success('Utilisateur mis à jour');
      setEditing(null);
      load();
    } catch (e2) { toast.error(e2.response?.data?.detail || 'Erreur'); }
    setSaving(false);
  };

  const toggleActive = async (u) => {
    if (!window.confirm(`${u.active === false ? 'Réactiver' : 'Désactiver'} l'accès de ${u.name} ?`)) return;
    try {
      await api.put(`/superadmin/users/${u.id}`, { active: u.active === false });
      toast.success(u.active === false ? 'Utilisateur réactivé' : 'Utilisateur désactivé');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const openReset = (u) => {
    setResetting(u);
    setNewPassword('');
  };

  const confirmReset = async () => {
    if (!resetting) return;
    if (newPassword.length < 6) return toast.error('6 caractères minimum');
    try {
      await api.put(`/superadmin/users/${resetting.id}/reset-password`, { password: newPassword });
      toast.success('Mot de passe réinitialisé');
      setResetting(null);
      setNewPassword('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Supprimer définitivement ${u.name} (${u.email}) ?`)) return;
    try {
      await api.delete(`/superadmin/users/${u.id}`);
      toast.success('Utilisateur supprimé');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const openCreate = () => {
    setCreateForm({
      name: '', email: '', password: '', role: 'commercial',
      tenant_id: (operators[0]?.id) || 'flexcore', active: true, phone: '',
    });
    setCreating(true);
  };
  const submitCreate = async (e) => {
    e?.preventDefault?.();
    if (!createForm.name || !createForm.email || !createForm.password || !createForm.tenant_id) {
      return toast.error('Nom, email, mot de passe et opérateur requis');
    }
    if (createForm.password.length < 6) return toast.error('Mot de passe : 6 caractères min');
    setCreatingSave(true);
    try {
      await api.post('/superadmin/users', createForm);
      toast.success('Utilisateur créé');
      setCreating(false);
      load();
    } catch (e2) { toast.error(e2.response?.data?.detail || 'Erreur'); }
    setCreatingSave(false);
  };

  if (loading) return <div className="p-10 text-slate-500">Chargement...</div>;

  return (
    <div className="p-6 md:p-8 lg:p-10 space-y-5" data-testid="sa-users-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Utilisateurs (tous opérateurs)</h1>
          <p className="text-sm text-slate-400 mt-0.5">{users.length} utilisateur{users.length > 1 ? 's' : ''} au total</p>
        </div>
        <Button  onClick={openCreate} data-testid="sa-user-create-btn">
          <UserPlus className="w-4 h-4 mr-1" /> Créer un utilisateur
        </Button>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
        <Input className="h-9 pl-9 bg-white border-slate-200 text-slate-900 placeholder:text-slate-400" placeholder="Rechercher email, nom ou opérateur…" value={q} onChange={e => setQ(e.target.value)} data-testid="sa-users-search" />
      </div>

      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
        <table className="w-full text-[13px]">
          <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
            <tr>
              <th className="text-left px-5 py-2 font-medium">Opérateur</th>
              <th className="text-left px-5 py-2 font-medium">Nom</th>
              <th className="text-left px-5 py-2 font-medium">Email</th>
              <th className="text-left px-5 py-2 font-medium">Rôle</th>
              <th className="text-left px-5 py-2 font-medium">Statut</th>
              <th className="text-right px-5 py-2 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody className="text-slate-300">
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="text-center py-6 text-slate-500 text-[12px]">Aucun résultat</td></tr>
            )}
            {filtered.map(u => {
              const isActive = u.active !== false;
              return (
                <tr key={u.id} className="border-t border-slate-100" data-testid={`sa-user-row-${u.id}`}>
                  <td className="px-5 py-2.5 text-slate-500">{u.tenant_nom || '—'}</td>
                  <td className="px-5 py-2.5 text-slate-900 font-medium">{u.name}</td>
                  <td className="px-5 py-2.5 text-slate-400">{u.email}</td>
                  <td className="px-5 py-2.5">
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">
                      {ROLE_LABEL[u.role] || u.role}
                    </span>
                  </td>
                  <td className="px-5 py-2.5">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${isActive ? 'bg-emerald-500/15 text-emerald-300' : 'bg-rose-500/15 text-rose-300'}`}>
                      {isActive ? 'Actif' : 'Désactivé'}
                    </span>
                  </td>
                  <td className="px-5 py-2.5 text-right space-x-1">
                    <button onClick={() => openEdit(u)} title="Modifier" aria-label="Modifier" className="p-1.5 rounded-md hover:bg-slate-100 text-slate-400 hover:text-slate-900 transition-colors" data-testid={`sa-user-edit-${u.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                    <button onClick={() => openReset(u)} title="Réinitialiser mot de passe" aria-label="Reset password" className="p-1.5 rounded-md hover:bg-slate-100 text-slate-400 hover:text-amber-600 transition-colors" data-testid={`sa-user-reset-${u.id}`}><KeyRound className="w-3.5 h-3.5" /></button>
                    <button onClick={() => toggleActive(u)} title={isActive ? 'Désactiver' : 'Réactiver'} aria-label="Toggle actif" className="p-1.5 rounded-md hover:bg-slate-100 text-slate-400 hover:text-cyan-600 transition-colors" data-testid={`sa-user-toggle-${u.id}`}><Power className="w-3.5 h-3.5" /></button>
                    <button onClick={() => remove(u)} title="Supprimer" aria-label="Supprimer" className="p-1.5 rounded-md hover:bg-slate-100 text-slate-400 hover:text-rose-600 transition-colors" data-testid={`sa-user-delete-${u.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Dialog création */}
      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="max-w-md" data-testid="sa-user-create-dialog">
          <DialogHeader>
            <DialogTitle>Créer un utilisateur</DialogTitle>
            <DialogDescription>Rattacher un sous-compte à un opérateur avec un rôle spécifique</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitCreate} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px]">Nom complet *</Label>
                <Input className="h-9" value={createForm.name} onChange={e => setCreateForm(f => ({ ...f, name: e.target.value }))} data-testid="sa-user-create-name" />
              </div>
              <div>
                <Label className="text-[11px]">Téléphone</Label>
                <Input className="h-9" value={createForm.phone} onChange={e => setCreateForm(f => ({ ...f, phone: e.target.value }))} />
              </div>
            </div>
            <div>
              <Label className="text-[11px]">Email *</Label>
              <Input className="h-9" type="email" value={createForm.email} onChange={e => setCreateForm(f => ({ ...f, email: e.target.value }))} data-testid="sa-user-create-email" />
            </div>
            <div>
              <Label className="text-[11px]">Mot de passe * (6 caractères min)</Label>
              <Input className="h-9" type="text" value={createForm.password} onChange={e => setCreateForm(f => ({ ...f, password: e.target.value }))} data-testid="sa-user-create-password" placeholder="Ex. Bienvenue2026!" />
              <BoutonMotDePasse valeur={createForm.password} tid="sa-compte-mdp"
                                onGenere={(m) => setCreateForm(f => ({ ...f, password: m }))} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px]">Rôle *</Label>
                <select className="h-9 w-full rounded-md border border-input bg-transparent px-2 text-[12px]" value={createForm.role} onChange={e => setCreateForm(f => ({ ...f, role: e.target.value }))} data-testid="sa-user-create-role">
                  {ROLE_OPTIONS.map(r => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-[11px]">Opérateur *</Label>
                <select className="h-9 w-full rounded-md border border-input bg-transparent px-2 text-[12px]" value={createForm.tenant_id} onChange={e => setCreateForm(f => ({ ...f, tenant_id: e.target.value }))} data-testid="sa-user-create-operator">
                  <option value="">— Sélectionner —</option>
                  {operators.map(op => <option key={op.id} value={op.id}>{op.name}</option>)}
                </select>
              </div>
            </div>
            <label className="flex items-center gap-2 text-[12px] cursor-pointer">
              <input type="checkbox" checked={createForm.active} onChange={e => setCreateForm(f => ({ ...f, active: e.target.checked }))} />
              <span>Compte actif immédiatement</span>
            </label>
            <p className="text-[10px] text-neutral-500">
              <strong>Rappel matrice des droits :</strong> Admin (accès complet opérateur), Gestionnaire (CRM et supervision, ni estimations ni pilotage), Distributeur (SAV lecture seule), Installateur (CFTSign uniquement).
            </p>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreating(false)}>Annuler</Button>
              <Button type="submit" disabled={creatingSave} data-testid="sa-user-create-save">{creatingSave ? '…' : 'Créer'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog édition */}
      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent className="max-w-md" data-testid="sa-user-edit-dialog">
          <DialogHeader>
            <DialogTitle>Modifier l&apos;utilisateur</DialogTitle>
            <DialogDescription>{editing?.email}</DialogDescription>
          </DialogHeader>
          <form onSubmit={saveEdit} className="space-y-3">
            <div>
              <Label className="text-[11px]">Nom complet</Label>
              <Input className="h-9" value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} data-testid="sa-user-edit-name" />
            </div>
            <div>
              <Label className="text-[11px]">Email</Label>
              <Input className="h-9" type="email" value={editForm.email} onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))} data-testid="sa-user-edit-email" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px]">Rôle</Label>
                <select className="h-9 w-full rounded-md border border-input bg-transparent px-2 text-[12px]" value={editForm.role} onChange={e => setEditForm(f => ({ ...f, role: e.target.value }))} data-testid="sa-user-edit-role">
                  {ROLE_OPTIONS.map(r => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-[11px]">Opérateur</Label>
                <select className="h-9 w-full rounded-md border border-input bg-transparent px-2 text-[12px]" value={editForm.tenant_id} onChange={e => setEditForm(f => ({ ...f, tenant_id: e.target.value }))} data-testid="sa-user-edit-operator">
                  {operators.map(op => <option key={op.id} value={op.id}>{op.name}</option>)}
                </select>
              </div>
            </div>
            <div>
              <Label className="text-[11px]">Téléphone</Label>
              <Input className="h-9" value={editForm.phone} onChange={e => setEditForm(f => ({ ...f, phone: e.target.value }))} />
            </div>
            <label className="flex items-center gap-2 text-[12px] cursor-pointer">
              <input type="checkbox" checked={editForm.active} onChange={e => setEditForm(f => ({ ...f, active: e.target.checked }))} data-testid="sa-user-edit-active" />
              <span>Compte actif</span>
            </label>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditing(null)}>Annuler</Button>
              <Button type="submit" disabled={saving} data-testid="sa-user-edit-save">{saving ? '…' : 'Enregistrer'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog reset password */}
      <Dialog open={!!resetting} onOpenChange={(o) => { if (!o) { setResetting(null); setNewPassword(''); } }}>
        <DialogContent className="max-w-md" data-testid="sa-user-reset-dialog">
          <DialogHeader>
            <DialogTitle>Réinitialiser le mot de passe</DialogTitle>
            <DialogDescription>
              {resetting?.name} — {resetting?.email}
              <br />
              L&apos;ancien mot de passe sera immédiatement invalidé. Communiquez le nouveau à l&apos;utilisateur par un canal sécurisé.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-[11px]">Nouveau mot de passe (6 caractères min)</Label>
            <Input className="h-9" type="text" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Ex. Temp2026!" data-testid="sa-user-reset-input" />
            <Button type="button" variant="outline" size="sm" onClick={() => setNewPassword(Math.random().toString(36).slice(-10) + '!')} data-testid="sa-user-reset-generate">
              Générer un mot de passe aléatoire
            </Button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setResetting(null); setNewPassword(''); }}>Annuler</Button>
            <Button onClick={confirmReset} disabled={newPassword.length < 6} data-testid="sa-user-reset-confirm">Réinitialiser</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
